import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchConstellationMe } from '../api/constellation';
import { ConstellationStatsBar } from '../components/constellation/ConstellationStatsBar';
import { ConstellationCanvas } from '../components/constellation/ConstellationCanvas';
import { ConstellationProfileCard } from '../components/constellation/ConstellationProfileCard';
import { DegradedBanner } from '../components/constellation/DegradedBanner';
import { DiscoveryStatusBanner } from '../components/discovery/DiscoveryStatusBanner';
import { UniverseWatchtower } from '../components/constellation/UniverseWatchtower';
import UniverseVibeControls from '../components/constellation/UniverseVibeControls';
import { useUniversePrefs } from '../components/constellation/useUniversePrefs';
import { resolveUniverseTheme } from '../components/constellation/universeThemes';
import { track } from '../lib/analytics';
import { UNIVERSE_CANCEL_EVENT } from '../hooks/useSafeLoader';

// API helper for opening chat with timeout
const API_BASE = window.location.origin;
const API_TIMEOUT_MS = 10000; // 10 second timeout
const ACTION_WATCHDOG_MS = 8000; // 8 second watchdog for loading states
const CONSTELLATION_TIMEOUT_MS = 12000; // 12s hard timeout for initial load

async function openChatWithUser(userId) {
  const token = localStorage.getItem('token');
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);
  
  const url = `${API_BASE}/api/chat/open`;
  console.log("[openChatWithUser] url", url);
  console.log("[openChatWithUser] has token", !!token);
  console.log("[openChatWithUser] user_id", userId);
  
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user_id: userId }),
      signal: controller.signal,
    });
    
    console.log("[openChatWithUser] status", res.status);
    
    // Handle 403 = not matched
    if (res.status === 403) {
      return { status: 'not_matched', conversation_id: null, match_id: null };
    }
    
    if (!res.ok) {
      console.error("[openChatWithUser] non-OK status", res.status);
      return null;
    }
    
    // Safe text read
    let text = '';
    try {
      text = await res.text();
      console.log("[openChatWithUser] text", text?.slice(0, 300));
    } catch (e) {
      console.error("[openChatWithUser] res.text() failed", e);
      return null;
    }
    
    // Safe JSON parse
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch (e) {
      console.error("[openChatWithUser] JSON parse error", e, "raw:", text?.slice(0, 200));
      return null;
    }
    
    console.log("[openChatWithUser] parsed data", data);
    return data;
    
  } catch (err) {
    console.error("[openChatWithUser] caught error", err);
    if (err.name === 'AbortError') {
      console.error("[openChatWithUser] request timed out");
    }
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Constellation Screen - Milestone 4: Frontend Navigation + Screen Shell
 *                      + Milestone 5: Canvas Renderer
 *                      + Milestone 6: Interaction Layer
 * 
 * Main screen for the Constellation System
 * 
 * States:
 * - Loading: Fetching constellation data
 * - Error: API failed, show retry button
 * - Empty: User has no connections yet
 * - Loaded: Show stats + interactive star map
 * 
 * Interactions (M6):
 * - Tap/click a star to view profile card
 * - View profile or open chat from card
 */
export const ConstellationScreen = () => {
  const navigate = useNavigate();
  const location = useLocation(); // For modal routing (background location pattern)
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [isDegraded, setIsDegraded] = useState(false);
  
  // Discovery status banner state
  const [discoveryMeta, setDiscoveryMeta] = useState(null);
  const [discoveryDebug, setDiscoveryDebug] = useState(null);
  
  // Check if user is admin (from localStorage user data)
  const isAdmin = (() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        return user?.is_admin === true;
      }
    } catch (e) { /* ignore */ }
    return false;
  })();
  
  // Tap-after-zoom debug state
  const [hitDebug, setHitDebug] = useState(null);
  
  // FIX: Ref to track selectedNode synchronously (avoids stale closure in event handlers)
  const selectedNodeRef = useRef(null);
  useEffect(() => {
    selectedNodeRef.current = selectedNode;
  }, [selectedNode]);
  
  // WATCHDOG: Ref for action loading timeout
  const actionTimerRef = useRef(null);
  
  // REQUEST ID: Prevents stale responses from affecting UI
  const requestIdRef = useRef(0);
  
  // Universe theme preferences
  const { prefs, setPrefs } = useUniversePrefs();
  const theme = resolveUniverseTheme(prefs.theme, prefs);
  
  // WATCHDOG: Start loading with auto-kill after 8s
  const startActionLoading = () => {
    setActionLoading(true);
    
    // Hard kill switch - even if fetch never returns
    if (actionTimerRef.current) clearTimeout(actionTimerRef.current);
    actionTimerRef.current = setTimeout(() => {
      console.warn('[Constellation] Watchdog triggered - force clearing actionLoading');
      setActionLoading(false);
    }, ACTION_WATCHDOG_MS);
  };
  
  // WATCHDOG: Stop loading and clear timer
  const stopActionLoading = () => {
    setActionLoading(false);
    if (actionTimerRef.current) {
      clearTimeout(actionTimerRef.current);
      actionTimerRef.current = null;
    }
  };
  
  // Cleanup watchdog on unmount
  useEffect(() => {
    return () => {
      if (actionTimerRef.current) clearTimeout(actionTimerRef.current);
    };
  }, []);
  
  // Force cancel loading when global cancel event is triggered
  // (e.g., when modal closes via back button)
  const forceStopLoading = useCallback((detail = {}) => {
    console.log('[ConstellationScreen] Global cancel event received - clearing loading', detail);

    // Invalidate any in-flight request or late resolution
    requestIdRef.current += 1;

    setLoading(false);
    setError(null);
    stopActionLoading();
  }, []);
  
  useEffect(() => {
    window.addEventListener(UNIVERSE_CANCEL_EVENT, forceStopLoading);
    return () => window.removeEventListener(UNIVERSE_CANCEL_EVENT, forceStopLoading);
  }, [forceStopLoading]);
  
  // Show toast helper
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };
  
  // Proper close handler that resets ALL state
  const closeCard = () => {
    stopActionLoading();
    setSelectedNode(null);
  };

  const loadConstellation = useCallback(async () => {
    const currentRequestId = ++requestIdRef.current;

    setLoading(true);
    setError(null);
    setDiscoveryMeta(null);
    setDiscoveryDebug(null);

    let timeoutId = null;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error('Constellation load timed out'));
      }, CONSTELLATION_TIMEOUT_MS);
    });

    try {
      const res = await Promise.race([fetchConstellationMe(), timeoutPromise]);

      // Ignore stale responses
      if (currentRequestId !== requestIdRef.current) return;

      setData(res);
      
      // Capture discovery meta and debug from response
      if (res?.meta) {
        setDiscoveryMeta(res.meta);
      }
      if (res?.debug) {
        setDiscoveryDebug(res.debug);
      }

      // Check for degraded mode from response
      setIsDegraded(res?._degraded === true);
      if (res?._degraded) {
        console.warn('[Constellation] Backend returned degraded mode', { count: res._degradedCount });
        track("universe_degraded_mode", { degraded_count: res._degradedCount });
      }

      track("universe_opened", {
        source: document.referrer?.includes("/dashboard") ? "dashboard_card" : "direct_url",
        nodes: res?.nodes?.length ?? 0,
        edges: res?.edges?.length ?? 0,
        degraded: res?._degraded || false
      });
    } catch (err) {
      console.error('[Constellation] Failed to load:', err);

      if (currentRequestId !== requestIdRef.current) return;

      setError('Unable to load your universe. Please try again.');
      
      // Set error meta for banner
      setDiscoveryMeta({
        status: 'error',
        searched: true,
        result_count: 0,
        message: err?.message?.includes('timed out') 
          ? 'Request timed out — tap to retry'
          : 'Something went wrong — tap to retry',
      });
      
      track("universe_error", {
        error_type: err?.message?.includes('timed out') ? "api_timeout" : "api_load_failed",
        error_message: err?.message || "Unknown error"
      });
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadConstellation();
  }, [loadConstellation]);

  const handleRetry = () => {
    // hard cancel any stale requests and retry cleanly
    requestIdRef.current += 1;
    setError(null);
    loadConstellation();
  };

  // M6: Node selection handler
  // FIX: Use ref to avoid stale closure race condition
  const handleNodeSelected = (node) => {
    const current = selectedNodeRef.current;
    
    console.log('[ConstellationScreen] handleNodeSelected:', node ? node.display_name || node.user_id : 'null', '| current:', current ? current.display_name : 'null');

    // If a node is already selected, ignore clears coming from canvas "miss"
    if (node === null && current !== null) {
      console.log('[ConstellationScreen] Ignoring clear - profile card is open (ref guard)');
      return;
    }

    setSelectedNode(node);
  };

  // M6: Profile card action handlers
  // FIX #4: View Profile ONLY navigates to profile, NEVER to chat
  // MODAL ROUTING: Opens profile as overlay on top of Universe
  const handleViewProfile = (userId) => {
    console.log('[ConstellationScreen] VIEW PROFILE handler - userId:', userId);
    
    if (!userId) {
      showToast('Unable to view profile - missing user ID', 'error');
      return;
    }
    
    // FIX #1: closeCard + rAF before navigate (mobile Safari click fix)
    closeCard();
    requestAnimationFrame(() => {
      // MODAL ROUTING: Pass current location as backgroundLocation
      // This keeps Universe mounted while Profile renders as overlay
      navigate(`/profile/${String(userId)}`, { 
        state: { backgroundLocation: location } 
      });
    });
  };

  // FIX #4: Open Chat ONLY opens chat, NEVER navigates to profile
  // Accepts { matchId, userId } object - matchId takes priority (fast path)
  // If only userId provided, calls /api/chat/open to get matchId (slow path)
  const handleOpenChat = async (params) => {
    // Support both old format (userId string) and new format ({ matchId, userId })
    const { matchId, userId } = typeof params === 'object' 
      ? params 
      : { matchId: null, userId: params };
    
    console.log('[ChatClick] params:', { matchId, userId });
    console.log('[ChatClick] selectedNode:', selectedNode?.display_name || selectedNode?.user_id || 'NULL');
    
    // FIX #3: No-op guard - catch missing IDs early
    if (!matchId && !userId) {
      console.error('[ChatClick] BLOCKED - missing both matchId and userId');
      showToast('Cannot open chat: missing matchId and userId', 'error');
      return;
    }
    
    // FAST PATH: If we already have matchId, navigate directly!
    // FIX #1: closeCard + rAF before navigate (mobile Safari click fix)
    // MODAL ROUTING: Pass backgroundLocation to keep Universe mounted
    if (matchId) {
      console.log('[ChatClick] FAST PATH - navigating to /chat/' + matchId);
      closeCard(); // Use existing close logic, not raw setSelectedNode
      requestAnimationFrame(() => {
        navigate(`/chat/${String(matchId)}`, {
          state: { backgroundLocation: location }
        });
      });
      return;
    }
    
    // SLOW PATH: Need to call API to get matchId
    if (!userId) {
      console.error('[ChatClick] BLOCKED - no userId provided');
      showToast('Unable to open chat. Please try again.', 'error');
      return;
    }
    
    // Create unique request ID to detect stale responses
    const currentRequestId = ++requestIdRef.current;
    console.log("[ChatClick] start API call", { userId, requestId: currentRequestId, actionLoading });
    
    if (actionLoading) {
      console.log("[ChatClick] blocked - already loading");
      return;
    }
    startActionLoading();
    
    try {
      console.log("[ChatClick] calling POST /api/chat/open...");
      const chatData = await openChatWithUser(userId);
      
      // Guard: Ignore if this is a stale request
      if (currentRequestId !== requestIdRef.current) {
        console.log("[ChatClick] stale response ignored", { currentRequestId, latest: requestIdRef.current });
        return;
      }
      
      console.log("[ChatClick] chatData received:", chatData);
      
      // DEFENSIVE: Check if chatData is valid
      if (!chatData || typeof chatData !== 'object') {
        console.error("[ChatClick] invalid chatData", chatData);
        showToast('Connection error. Please try again.', 'error');
        return;
      }
      
      // SUCCESS: Navigate to chat - use match_id first (Chat.js expects matchId)
      // FIX: Priority is match_id > conversation_id
      const chatId = chatData.match_id || chatData.conversation_id;
      console.log("[ChatClick] resolved chatId:", chatId, "from", { match_id: chatData.match_id, conversation_id: chatData.conversation_id });
      
      if (chatData.status === 'matched' && chatId) {
        console.log("[ChatClick] SUCCESS - navigating to", `/chat/${chatId}`);
        // FIX #1: closeCard + rAF before navigate (mobile Safari click fix)
        // MODAL ROUTING: Pass backgroundLocation to keep Universe mounted
        closeCard();
        requestAnimationFrame(() => {
          navigate(`/chat/${String(chatId)}`, {
            state: { backgroundLocation: location }
          });
        });
        return;
      }
      
      // Edge case: matched but no ID (should never happen)
      if (chatData.status === 'matched' && !chatId) {
        console.error("[ChatClick] matched but no chatId!", chatData);
        showToast('Chat unavailable. Please try again.', 'error');
        return;
      }
      
      // NOT MATCHED: Show helpful toast
      if (chatData.status === 'not_matched') {
        console.log("[ChatClick] not matched -> showing toast");
        showToast('Chat unlocks after a mutual match 💫 Go to Discover to match.', 'info');
        return;
      }
      
      // UNEXPECTED: Unknown status
      console.warn("[ChatClick] unexpected status", chatData.status);
      showToast('Connection error. Please try again.', 'error');
      
    } catch (err) {
      // Guard: Ignore errors from stale requests
      if (currentRequestId !== requestIdRef.current) {
        console.log("[ChatClick] stale error ignored");
        return;
      }
      console.error('[ChatClick] error:', err);
      showToast('Connection error. Please try again.', 'error');
    } finally {
      // Guard: Only stop loading if this is still the current request
      if (currentRequestId === requestIdRef.current) {
        console.log("[ChatClick] finally - stopping loading");
        stopActionLoading();
      }
    }
  };

  const hasConnections = data && data.nodes && data.nodes.length > 0;

  return (
    <div className="flex flex-col h-screen bg-black text-white">
      {/* Header */}
      <header className="p-4 flex items-center justify-between border-b border-gray-800 relative z-50">
        <button 
          type="button"
          onClick={() => {
            // Cancel any in-flight loads before navigating away
            window.dispatchEvent(new CustomEvent(UNIVERSE_CANCEL_EVENT, { detail: { source: "Universe:header_back" } }));
            // FIX: navigate(-1) with fallback route
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate('/dashboard');
            }
          }} 
          className="text-sm text-gray-400 hover:text-white transition"
        >
          ← Back
        </button>
        <h1 className="text-base font-semibold">My Universe</h1>
        {/* FIX: Wire the 3-dot menu to real actions or hide it */}
        <div className="relative">
          <button 
            type="button"
            onClick={() => {
              // Reset view - dispatch event to canvas
              window.dispatchEvent(new CustomEvent('constellation:resetView'));
            }}
            className="text-xl text-gray-400 hover:text-white transition px-2"
            title="Reset View"
          >
            ↺
          </button>
        </div>
      </header>

      {/* Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Universe Watchtower - Admin monitoring overlay */}
        <UniverseWatchtower 
          isAdmin={isAdmin} 
          defaultExpanded={false}
          showForNonAdmin={false}
        />
        
        {/* Discovery Status Banner - shows search status to users */}
        <div className="px-4 py-2">
          <DiscoveryStatusBanner
            isLoading={loading}
            meta={discoveryMeta}
            debug={discoveryDebug}
            isAdmin={isAdmin}
            onRetry={handleRetry}
            compact={false}
          />
        </div>
        
        {/* Degraded Mode Banner */}
        <DegradedBanner isDegraded={isDegraded} onRefresh={loadConstellation} />
        
        {/* Loading State */}
        {loading && (
          <div className="flex-1 flex flex-col justify-center items-center gap-3 px-6">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-purple-500"></div>
            <div className="text-sm text-gray-400">
              Loading your constellation...
            </div>
            {/* Safety escape hatch - show after initial render */}
            <button
              onClick={() => {
                requestIdRef.current += 1; // invalidate in-flight
                setError(null);
                loadConstellation(); // let loadConstellation control loading state
              }}
              className="mt-4 text-xs text-gray-500 hover:text-gray-300 underline"
            >
              Taking too long? Tap to retry
            </button>
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="flex-1 flex flex-col justify-center items-center gap-4 px-6 text-center">
            <div className="text-lg">🌌</div>
            <div className="text-base font-medium text-white">
              Could not load your universe
            </div>
            <div className="text-sm text-gray-400 max-w-sm">
              Something went off in the stars. Try again in a moment.
            </div>
            <button
              onClick={handleRetry}
              className="px-6 py-2 rounded-full bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium transition active:scale-95"
            >
              Retry
            </button>
          </div>
        )}

        {/* Loaded State */}
        {!loading && !error && data && (
          <>
            {/* Stats Bar */}
            <ConstellationStatsBar data={data} />
            
            {/* Universe Vibe Controls */}
            <div className="px-4 mt-3">
              <UniverseVibeControls prefs={prefs} setPrefs={setPrefs} />
            </div>

            {/* Main Constellation Area - M5: Canvas Renderer + M6: Interactions */}
            {hasConnections ? (
              <div className="flex-1 relative">
                {/* Canvas wrapper - pointer-events disabled when card is open */}
                <div 
                  className="absolute inset-0"
                  style={{ pointerEvents: selectedNode ? 'none' : 'auto' }}
                >
                  <ConstellationCanvas
                    coreStar={data.core_star}
                    nodes={data.nodes}
                    edges={data.edges}
                    onNodeSelected={handleNodeSelected}
                    disableClear={!!selectedNode}
                    theme={theme}
                    onHitDebug={setHitDebug}
                  />
                </div>
                
                {/* Profile Card layer - inside same relative parent for proper z-index */}
                {selectedNode && (
                  <div className="absolute inset-0 z-[2147483647] pointer-events-auto">
                    <ConstellationProfileCard
                      node={selectedNode}
                      onClose={closeCard}
                      onViewProfile={handleViewProfile}
                      onOpenChat={handleOpenChat}
                      actionLoading={actionLoading}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center px-6">
                <div className="text-center">
                  <div className="mb-4 text-4xl">✨</div>
                  <div className="text-base text-gray-300 mb-2">
                    Your universe is waiting for you
                  </div>
                  <div className="text-sm text-gray-400 max-w-md">
                    As you match, chat, and connect,<br/>
                    your stars will begin to appear here.
                  </div>
                  <button
                    onClick={() => navigate('/discover')}
                    className="mt-6 px-6 py-2 rounded-full bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium transition active:scale-95"
                  >
                    Start Discovering
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-20 left-1/2 -translate-x-1/2 z-[999999] px-4 py-2 rounded-full text-sm font-medium shadow-lg animate-fade-in ${
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-gray-800 text-white border border-gray-600'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default ConstellationScreen;
